import { useEffect, useRef } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useInitializeCurrentUser, useIsCurrentUserHydrated, useRefreshCurrentUser } from '@/hooks/useCurrentUser';
import { useIsUserAuthenticated } from '@/hooks/useIsUserAuthenticated';
import { useAppStore } from '@/store';

// why: must mount inside ApiClientProvider so auth/brand-uuid interceptors are wired before the
// why: /me call fires.
// why: and it must also WAIT for the hub brand to resolve. Wiring the interceptor is not the same as
// why: knowing the brand: firing /me first sends no x-brand-uuid, so the API falls back to the brand
// why: baked into the token. With several hubs embedded on one page that token belongs to whichever
// why: iframe won the shared-storage race, so every tab resolved "who am I" against the WRONG hub.
// gloss
export const CurrentUserInitializer = (): null => {
  const isAuthenticated = useIsUserAuthenticated();
  const isHubKnown = useAppStore(
    (s) => s.hubBrand.isResolved && Boolean(s.hubBrand.uuid || s.hubBrand.brandID || s.hubBrand.code),
  );
  const hubUuid = useAppStore((s) => s.hubBrand.uuid);
  const client = useApiClient();
  const initialize = useInitializeCurrentUser();
  const refreshMe = useRefreshCurrentUser();
  const isHydrated = useIsCurrentUserHydrated();
  const hydratedForHub = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isAuthenticated || !isHubKnown) return;
    if (!isHydrated) {
      hydratedForHub.current = hubUuid;
      void initialize(client);
      return;
    }
    if (hydratedForHub.current === undefined) {
      hydratedForHub.current = hubUuid;
      return;
    }
    if (hydratedForHub.current !== hubUuid) {
      hydratedForHub.current = hubUuid;
      void refreshMe(client);
    }
  }, [isAuthenticated, isHubKnown, hubUuid, isHydrated, client, initialize, refreshMe]);

  return null;
};
